import { type CSSProperties, type ReactElement, useEffect } from 'react';

import loadable from '@loadable/component';
import { BpkContentBubble } from '@skyscanner-internal/backpack-web/bpk-component-flare';
import BpkOverlay, {
  OVERLAY_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-overlay';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { PRODUCT_GROUP } from '@skyscanner-internal/landing-pages-shared-constants/constants';
import classNames from 'classnames';

import { logExperimentChokepoint } from '../../logMiniEvent';

import type { SearchControlsProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/CarHireSearchControlsProps';
import type { FlightsSearchControlsGCWrapperProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FlightsSearchControlsGCWrapperProps';
import type { PackagesSearchControlsGCWrapperProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/PackagesSearchControlsProps';
import type { SearchControlsWrapperProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/SearchControlsWrapper';
import type { Vertical } from '@skyscanner-internal/landing-pages-shared-types/types-shared/Vertical';
import type { HotelsSearchControlsGCWrapperProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/hotels-components/HotelsSearchControlsGCWrapperProps';

import STYLES from './SearchControlsWrapper.module.scss';

const searchControlsMap = {
  [PRODUCT_GROUP.FLIGHTS]: loadable(
    () => import('../FlightsSearchControlsGCWrapper'),
  ),
  [PRODUCT_GROUP.HOTELS]: loadable(
    () => import('../HotelsSearchControlsGCWrapper'),
  ),
  [PRODUCT_GROUP.CAR_HIRE]: loadable(() => import('../CarHireSearchControls')),
  [PRODUCT_GROUP.PACKAGES]: loadable(
    () => import('../PackagesSearchControlsGCWrapper'),
  ),
};

const SearchControls = (
  vertical: Vertical,
  carHireProps?: SearchControlsProps,
  hotelsProps?: HotelsSearchControlsGCWrapperProps,
  packagesGCProps?: PackagesSearchControlsGCWrapperProps,
  background?: string,
  flightsGCProps?: FlightsSearchControlsGCWrapperProps,
) => {
  const searchWrapperClasses = [STYLES.SearchWrapper];

  if (background) {
    searchWrapperClasses.push(STYLES.SearchWrapperLarge);
  }

  switch (vertical) {
    case PRODUCT_GROUP.FLIGHTS: {
      const VerticalSearchControls = searchControlsMap[vertical];

      return (
        <div
          data-testid="flights-search-controls-gc"
          className={classNames(searchWrapperClasses)}
        >
          <VerticalSearchControls {...flightsGCProps} />
        </div>
      );
    }
    case PRODUCT_GROUP.HOTELS: {
      if (!hotelsProps) {
        throw Error('Vertical is Hotels but hotelsProps is empty');
      }

      const VerticalSearchControls = searchControlsMap[vertical];
      return (
        <div
          data-testid="hotels-search-controls-gc"
          className={classNames(
            searchWrapperClasses,
            STYLES.HotelsSearchControlsGCWrapper,
          )}
        >
          <VerticalSearchControls {...hotelsProps} />
        </div>
      );
    }
    case PRODUCT_GROUP.CAR_HIRE: {
      if (!carHireProps) {
        throw Error('Vertical is CarHire but carHireProps is empty');
      }
      const VerticalSearchControls = searchControlsMap[vertical];
      return (
        <div
          data-testid="car-hire-search-controls"
          className={classNames(searchWrapperClasses)}
        >
          <VerticalSearchControls {...carHireProps} />
        </div>
      );
    }
    case PRODUCT_GROUP.PACKAGES: {
      if (!packagesGCProps) {
        throw Error('Vertical is Packages but packagesGCProps is empty');
      }
      const VerticalSearchControls = searchControlsMap[vertical];
      return (
        <div
          data-testid="packages-search-controls-gc"
          className={classNames(searchWrapperClasses)}
        >
          <VerticalSearchControls {...packagesGCProps} />
        </div>
      );
    }
    default: {
      throw Error(`SearchControlsWrapper: Unrecognized vertical: ${vertical}`);
    }
  }
};

const SearchControlsWrapper = ({
  background,
  carHireProps,
  flightsGCProps,
  h1,
  hotelsProps,
  isMainTitle = true,
  layout = 'text',
  packagesGCProps,
  pageType,
  showFlare = false,
  travelTrendsStyle = false,
  vertical,
}: SearchControlsWrapperProps) => {
  useEffect(() => {
    if (
      vertical === PRODUCT_GROUP.PACKAGES &&
      pageType === 'package-holidays-home'
    ) {
      logExperimentChokepoint('packages_confidence_messaging_experiment');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const innerContentClasses = [STYLES.InnerContent];

  const showBackgroundOverlay = pageType === 'hotels-home' && !!background;
  const showCarHireCombinedCalendar =
    vertical === PRODUCT_GROUP.CAR_HIRE &&
    !!carHireProps?.enableCombinedCalendar;
  if (showBackgroundOverlay) {
    innerContentClasses.push(STYLES.HeroWithShadow);
  }

  if (background) {
    // Use InnerContentMedium for heroOverlay, InnerContentLarge for other layouts
    if (layout === 'heroOverlay') {
      innerContentClasses.push(STYLES.InnerContentMedium);
    } else {
      innerContentClasses.push(STYLES.InnerContentLarge);
    }
  }

  let InnerElement: ReactElement = <div />;

  if (layout === 'heroOverlay' && background) {
    const heroOverlayContent = (
      <div className={classNames(STYLES.Content, STYLES.HeroOverlayContent)}>
        <BpkText
          color={TEXT_COLORS.textOnDark}
          className={STYLES.Title}
          tagName={isMainTitle ? 'h1' : 'h2'}
          textStyle={TEXT_STYLES.hero5}
        >
          {h1}
        </BpkText>
        {SearchControls(
          vertical,
          carHireProps,
          hotelsProps,
          packagesGCProps,
          background,
          flightsGCProps,
        )}
      </div>
    );

    InnerElement = (
      <BpkOverlay
        className={STYLES.HeroOverlay}
        overlayType={OVERLAY_TYPES.topMedium}
        foregroundContent={heroOverlayContent}
      >
        <div
          className={STYLES.HeroOverlayBackground}
          style={{ backgroundImage: background }}
        />
      </BpkOverlay>
    );
  }

  if (layout === 'bubble' || layout === 'bubbleCentre') {
    InnerElement = (
      <div
        className={classNames(STYLES.Content, STYLES.BubbleContent, {
          [STYLES.BubbleCentreContent]: layout === 'bubbleCentre',
          [STYLES.TravelTrends]: travelTrendsStyle,
        })}
      >
        <div
          style={{ ['--bg-image' as keyof CSSProperties]: background }}
          className={STYLES.BubbleImage}
        />
        <BpkText
          color={TEXT_COLORS.textOnDark}
          className={STYLES.Title}
          tagName={isMainTitle ? 'h1' : 'h2'}
          textStyle={
            travelTrendsStyle ? TEXT_STYLES.hero4 : TEXT_STYLES.heading2
          }
        >
          {h1}
        </BpkText>
        {SearchControls(
          vertical,
          carHireProps,
          hotelsProps,
          packagesGCProps,
          background,
          flightsGCProps,
        )}
      </div>
    );
  }
  if (layout === 'text') {
    // Configuration for which vertical/pageType combinations need contrast fix styling
    const needsContrastFix =
      (vertical === PRODUCT_GROUP.PACKAGES &&
        pageType === 'destination-hub-city') ||
      (vertical === PRODUCT_GROUP.HOTELS &&
        pageType === 'destination-hub-neighbourhood');

    const getTextStyleValue = ({
      background: backgroundValue,
    }: {
      background?: string;
      vertical: Vertical;
      pageType?: string;
    }) => {
      if (needsContrastFix) {
        return TEXT_STYLES.heading2;
      }
      return backgroundValue ? TEXT_STYLES.hero4 : TEXT_STYLES.heading1;
    };

    InnerElement = (
      <div
        className={classNames(STYLES.Content, {
          [STYLES.SearchWrapperWithBackground]: needsContrastFix,
        })}
      >
        <BpkText
          color={TEXT_COLORS.textOnDark}
          className={STYLES.Title}
          tagName={isMainTitle ? 'h1' : 'h2'}
          textStyle={getTextStyleValue({ background, vertical, pageType })}
        >
          {h1}
        </BpkText>
        {SearchControls(
          vertical,
          carHireProps,
          hotelsProps,
          packagesGCProps,
          background,
          flightsGCProps,
        )}
      </div>
    );
  }

  return (
    <BpkContentBubble
      style={
        background
          ? {
              backgroundImage: background,
            }
          : {}
      }
      className={classNames(STYLES.Hero, {
        [STYLES.HeroWithShadow]: showBackgroundOverlay,
        [STYLES.HeroWithCarHireCalendar]: showCarHireCombinedCalendar,
      })}
      contentClassName={innerContentClasses.join(' ')}
      showPointer={showFlare}
      rounded={false}
      content={InnerElement}
      flareProps={{ className: STYLES.Flare, 'aria-hidden': true }}
    />
  );
};

export default SearchControlsWrapper;
